import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { MasterResume } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export const useMasterResumes = () => {
  const { user } = useAuth();
  const [resumes, setResumes] = useState<MasterResume[]>([]);
  const [loading, setLoading] = useState(false);

  const STORAGE_KEY = user ? `master_resumes_fallback_${user.id}` : null;

  // Helper to load from local storage
  const getLocalFallback = (): MasterResume[] => {
    if (!STORAGE_KEY) return [];
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  };

  const saveLocalFallback = (items: MasterResume[]) => {
    if (!STORAGE_KEY) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  };

  const fetchResumes = async () => {
    if (!user) return;
    setLoading(true);

    const localItems = getLocalFallback();
    setResumes(localItems); // Show local items immediately

    const { data, error } = await supabase
      .from("master_resumes")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      console.warn("Could not fetch master resumes from Supabase, using local fallback:", error.message);
    } else if (data) {
      const remoteResumes = data as MasterResume[];
      const localResumes = getLocalFallback();
      
      // Merge logic: ensure unsynced local items are preserved
      // We use content as a fallback signature to detect duplicates regardless of ID
      const unsynced = localResumes.filter(local => 
        !remoteResumes.some(remote => remote.content === local.content)
      );
      
      const merged = [...remoteResumes, ...unsynced];
      setResumes(merged);
      saveLocalFallback(merged);
    }
    setLoading(false);
  };

  const saveResume = async (name: string, content: string) => {
    if (!user) return;

    // Save locally first for instant feedback (optimistic update)
    const newResume: MasterResume = {
      id: crypto.randomUUID(),
      user_id: user.id,
      name,
      content,
      created_at: new Date().toISOString(),
    };
    
    const updated = [newResume, ...resumes];
    setResumes(updated);
    saveLocalFallback(updated);

    const { error } = await supabase
      .from("master_resumes")
      .insert({
        user_id: user.id,
        name,
        content,
      });

    if (error) {
      console.warn("Failed to sync resume to Supabase:", error.message);
      // We keep it in local storage so it's not lost
      toast.info("Saved locally (Cloud sync pending SQL setup)");
    } else {
      toast.success("Saved to your Master Resumes!");
    }
  };

  const deleteResume = async (id: string) => {
    const updated = resumes.filter((r) => r.id !== id);
    setResumes(updated);
    saveLocalFallback(updated);

    const { error } = await supabase
      .from("master_resumes")
      .delete()
      .eq("id", id)
      .eq("user_id", user?.id);

    if (error) {
      console.warn("Notice: Deleted from local only:", error.message);
    } else {
      toast.success("Removed from library");
    }
  };

  useEffect(() => {
    if (user) fetchResumes();
    else {
      setResumes([]);
    }
  }, [user]);

  return { resumes, loading, saveResume, deleteResume, refetch: fetchResumes };
};
